import { useState, useEffect } from 'react';
import { useEnv } from '../../../../context/env-context';
import { makeRequest } from '../../../../utils/makeRequest';
import StyledTableCell from '../../../../components/StyledTableCell';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { useAuth } from '../../../../hooks/useAuth';
import { Spinner } from '../../../../components/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';

function DashboardServicesHealth() {
  const {
    apiOriginAggregator,
    apiOriginConsumer,
    apiOriginMonitor,
    apiOriginMonitorWorker,
    apiOriginScheduler,
    apiOriginFunding,
    apiOriginOrchestrator,
    apiOriginNotifications,
    apiOriginReports,
  } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const origins = {
    aggregators: {
      name: 'Aggregators Manager',
      deploymentName: 'AggregatorsManager',
      origin: apiOriginAggregator,
    },
    consumers: {
      name: 'Consumers Manager',
      deploymentName: 'ConsumersManager',
      origin: apiOriginConsumer,
    },
    monitor: {
      name: 'Monitoring Service',
      deploymentName: 'Monitor',
      origin: apiOriginMonitor,
    },
    monitorWorker: {
      name: 'Monitoring Worker Service',
      deploymentName: 'MonitorWorker',
      origin: apiOriginMonitorWorker,
    },
    funding: {
      name: 'Funding Service',
      deploymentName: 'Funding',
      origin: apiOriginFunding,
    },
    scheduler: {
      name: 'Scheduler Service',
      deploymentName: 'Scheduler',
      origin: apiOriginScheduler,
    },
    orchestrator: {
      name: 'Orchestrator Service',
      deploymentName: 'Orchestrator',
      origin: apiOriginOrchestrator,
    },
    notifications: {
      name: 'Notifications Service',
      deploymentName: 'Notifications',
      origin: apiOriginNotifications,
    },
    reports: {
      name: 'Reports Service',
      deploymentName: 'Reports',
      origin: apiOriginReports,
    },
  };

  const servicesInitialState = Object.keys(origins).reduce(
    (acc, value) => ({
      ...acc,
      [value]: { status: 'Loading', environment: '---', version: '' },
    }),
    {},
  );

  const [spinner, setSpinner] = useState(false);
  const [services, setServices] = useState(servicesInitialState);
  const [deploymentInfo, setDeploymentInfo] = useState({ deploymentId: null, services: null });

  const getDeploymentInfo = async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = { token, url: `${apiOriginOrchestrator}/deployment_info`, method: 'GET' };

    try {
      const response = await makeRequest(config);

      setDeploymentInfo(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getServicesHealthCheck = async () => {
    setSpinner(true);
    await Promise.allSettled(
      Object.entries(origins).map(async ([key, value]) => {
        const config = { url: `${value.origin}/healthCheck`, method: 'GET' };

        try {
          const hc = await makeRequest(config);

          setServices(prevServices => ({
            ...prevServices,
            [key]: { status: 'Ok', environment: hc.environment, version: hc.version },
          }));
        } catch (error) {
          setServices(prevServices => ({
            ...prevServices,
            [key]: { status: 'Error', environment: '', version: '' },
          }));
        }
      }),
    );
    await getDeploymentInfo();
    setSpinner(false);
  };

  useEffect(() => {
    getServicesHealthCheck();
    const interval = setInterval(getServicesHealthCheck, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusBadge = {
    Ok: (
      <Badge className="bo-inline-badge" bg="success">
        On-line
      </Badge>
    ),
    Error: (
      <Badge className="bo-inline-badge" bg="danger">
        Off-line
      </Badge>
    ),
    Loading: 'Loading...',
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between">
        Services Health
        <Button className="bo-button-inline-link" onClick={getServicesHealthCheck}>
          Refresh
        </Button>
      </Card.Header>
      <Card.Body>
        <Spinner visible={spinner}>
          <div className="d-flex align-items-center gap-2 pb-2">
            <p className="py-0 px-2 m-0 fw-bold bo-badge">Deployment ID:</p>
            <Badge className="bo-inline-badge" bg="secondary">
              {deploymentInfo.deploymentId || '---'}
            </Badge>
          </div>
          <TableContainer>
            <Table aria-labelledby="tableTitle" aria-label="enhanced table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left" className="px-2">
                    Service Name
                  </StyledTableCell>
                  <StyledTableCell align="left" className="px-2">
                    Env.
                  </StyledTableCell>
                  <StyledTableCell align="left" className="px-2">
                    Origin
                  </StyledTableCell>
                  <StyledTableCell align="left" className="px-2">
                    Status
                  </StyledTableCell>
                  <StyledTableCell align="left" className="px-2">
                    Version
                  </StyledTableCell>
                  <StyledTableCell align="left" className="px-2" />
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(origins).map(([key, value]) => (
                  <TableRow key={key} hover tabIndex={-1}>
                    <StyledTableCell align="left" className="px-2">
                      <strong>{value.name}</strong>
                    </StyledTableCell>
                    <StyledTableCell align="left" className="px-2">
                      {services[key].environment}
                    </StyledTableCell>
                    <StyledTableCell align="left" className="px-2">
                      {value.origin}
                    </StyledTableCell>
                    <StyledTableCell align="left" className="px-2">
                      {statusBadge[services[key].status]}
                    </StyledTableCell>
                    <StyledTableCell align="left" className="px-2">
                      {services[key].version}
                    </StyledTableCell>
                    <StyledTableCell align="left" className="px-2 bo-min-w-60">
                      {deploymentInfo.services &&
                        (!deploymentInfo.services[value.deploymentName] ||
                          deploymentInfo.services[value.deploymentName].version !==
                            services[key].version.replace('v', '')) && (
                          <FontAwesomeIcon icon={faWarning} size="lg" className="text-danger" />
                        )}
                      {deploymentInfo.services &&
                        deploymentInfo.services[value.deploymentName]?.updated &&
                        deploymentInfo.services[value.deploymentName]?.version ===
                          services[key].version.replace('v', '') && (
                          <Badge className="bo-inline-badge" bg="warning">
                            New
                          </Badge>
                        )}
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Spinner>
      </Card.Body>
    </Card>
  );
}

export default DashboardServicesHealth;
